import { memo, useState, useEffect } from "react";
import { useForm, yupResolver } from "@mantine/form";
import {
  GetPrivateSessionQuery,
  CreatePrivateSessionLocationRoomsInput,
  useGetPrivateSessionLocationRoomsLazyQuery,
  useCreatePrivateSessionLocationRoomsMutation,
  useUpdatePrivateSessionLocationRoomsMutation,
  useGetLocationRoomsQuery,
  useDeletePrivateSessionLocationRoomsMutation,
  useGetLocationTaxRatesQuery,
} from "src/generated/graphql";
import { useModals } from "@mantine/modals";
import FormCard from "src/components/FormCard";
import { Button, Group, useMantineTheme } from "@mantine/core";
import { array, object, string } from "yup";
import Select, { ISelect } from "src/components/Select";
import MultiSelect from "src/components/MultiSelect";
import { GetPrivateSession } from "src/api/graphql/queries/PrivateSession";
import { useDisclosure } from "@mantine/hooks";
import { MESSAGES } from "src/utils/constants";
import { useAppContext } from "src/api/AppContext";

type IFormProps = Omit<
  CreatePrivateSessionLocationRoomsInput,
  "location" | "rooms" | "taxRates"
> & {
  location: string;
  rooms: string[];
  taxRates: string[];
};

type ISessionData = GetPrivateSessionQuery["privateSessionOne"];

interface LocationRoomDetailProps {
  sessionData: ISessionData;
  handleModal?: (id: string) => void;
  locationRoomId?: string | undefined;
  disabled?: boolean;
  title?: string;
  visibleCards: string[];
  setvisibleCards: (e: string[]) => void;
  currentLocation?: ISelect;
  isAllowWrite: boolean;
  isOrg: boolean;
}

const LocationRoomDetail = ({
  sessionData,
  locationRoomId,
  title,
  visibleCards,
  setvisibleCards,
  currentLocation,
  isAllowWrite,
  isOrg,
}: LocationRoomDetailProps) => {
  const theme = useMantineTheme();
  const modals = useModals();
  const { currentOrganization, currentLocation: selectedLocation } =
    useAppContext();
  const isTaxable = !!sessionData?.isTaxable;
  const [locationsData, setlocationsData] = useState<ISelect[]>([]);
  const [roomsData, setroomsData] = useState<ISelect[]>([]);
  const [isFormActive, { toggle: toggleFormActive }] = useDisclosure(
    !!locationRoomId
  );
  const isVisible = locationRoomId && visibleCards.includes(locationRoomId);
  const { data: locRoomsList } = useGetLocationRoomsQuery();
  const refetchQuery = [
    {
      query: GetPrivateSession,
      variables: { id: sessionData?.id! },
    },
  ];

  useEffect(() => {
    // if its location specific then by default select currentLocation
    if (!isOrg && selectedLocation) handleLocChange(selectedLocation.id);
  }, [isOrg, selectedLocation, locRoomsList]);

  const [getLocationRooms, { data: loctionRoomDetail }] =
    useGetPrivateSessionLocationRoomsLazyQuery({
      onCompleted() {
        getSetValues();
      },
      fetchPolicy: "network-only",
    });

  const getSetValues = () => {
    const { location, rooms, session, taxRates } =
      loctionRoomDetail?.privateSessionLocationRoomsOne || {};
    let _data = {
      rooms: rooms?.map((e) => e.id) || [],
      location: location?.id!,
      session: { id: session?.id! },
      taxRates: taxRates?.map((e) => e.id) || [],
    };
    handleLocChange(location?.id!);

    form.setValues(_data);
    form.resetDirty(_data);
  };

  // filter already added locations from dropdown selection
  useEffect(() => {
    let _locations = currentOrganization?.locations;
    if (_locations?.length && sessionData?.rooms.length) {
      _locations = _locations.filter((loc) => {
        const filteredIds = sessionData.rooms?.map((item) => item.location.id);
        return !filteredIds.includes(loc.id);
      });
    }
    _locations?.length &&
      setlocationsData(
        _locations?.map((val) => ({
          value: val.id,
          label: val.name,
        })) || []
      );
  }, [sessionData?.rooms]);

  const [createLocationRooms, { loading: createLoading }] =
    useCreatePrivateSessionLocationRoomsMutation({
      onCompleted: () => {
        modals.closeAll();
      },
      refetchQueries: refetchQuery,
    });

  const [updateLocationRooms, { loading: updateLoading }] =
    useUpdatePrivateSessionLocationRoomsMutation({
      onCompleted: (data) => {
        if (locationRoomId) {
          toggleFormActive();
          setvisibleCards(
            visibleCards.filter(
              (e) => e !== data.updatePrivateSessionLocationRooms.id
            )
          );
          form.resetDirty();
        } else {
          modals.closeAll();
        }
      },
      refetchQueries: refetchQuery,
    });
  const [deleteLocationRooms, { loading: deleteLoading }] =
    useDeletePrivateSessionLocationRoomsMutation({
      refetchQueries: refetchQuery,
    });

  const form = useForm<IFormProps>({
    initialValues: {
      session: { id: sessionData?.id! },
      location: "",
      rooms: [],
      taxRates: [],
    },
    validate: yupResolver(
      object().shape({
        location: string().required("Location is required"),
        // taxRates based on isTaxable
        ...(isTaxable && {
          taxRates: array()
            .min(1, "At least one Tax Rate is required")
            .required(),
        }),
      })
    ),
  });

  const handleDelete = () => {
    deleteLocationRooms({
      variables: { id: locationRoomId! },
    });
  };

  const handleSubmit = async (values: IFormProps) => {
    const { location, rooms, taxRates, ...rest } = values;
    let obj = {
      location: { id: location },
      rooms: rooms.map((e) => ({ id: e })),
      taxRates: isTaxable ? values.taxRates.map((e) => ({ id: e })) : [],
      ...rest,
    };
    locationRoomId
      ? updateLocationRooms({
          variables: {
            data: obj,
            id: locationRoomId,
          },
        })
      : createLocationRooms({
          variables: {
            data: obj,
          },
        });
  };

  const handleEdit = () => {
    toggleFormActive();
    setCardVisible();
  };

  const setCardVisible = () => {
    if (!isVisible && locationRoomId) {
      getLocationRooms({
        variables: { id: locationRoomId },
      });
      setvisibleCards([...visibleCards, locationRoomId]);
    }
  };

  const handleLocChange = (id: string) => {
    form.setFieldValue("location", id);
    setroomsData(
      locRoomsList?.locationMany
        .find((e) => e.id === id)
        ?.rooms.map((val) => ({
          value: val.id,
          label: val.name,
        })) || []
    );
  };

  const handleClick = () => {
    if (isVisible) {
      if (!isFormActive) {
        getSetValues();
        toggleFormActive();
      }
      setvisibleCards(visibleCards.filter((e) => e !== locationRoomId));
    } else {
      setCardVisible();
    }
  };

  const isFormUpdated = !isFormActive && form.isDirty() && form.isTouched();

  const selectedLoc = form.getInputProps("location").value;
  const { data: taxRates } = useGetLocationTaxRatesQuery({
    variables: { locationId: selectedLoc },
    skip: !selectedLoc,
  });

  return (
    <>
      <FormCard
        title={title || ""}
        bannerContent={isFormUpdated ? MESSAGES.UNSAVED_CHANGES : ""}
        loading={updateLoading || deleteLoading}
        onDelete={isAllowWrite ? handleDelete : undefined}
        onCancel={handleClick}
        onEdit={isAllowWrite ? handleEdit : undefined}
        onSave={form.onSubmit((values) => handleSubmit(values))}
        hideCard={!locationRoomId}
        mb={10}
        size="md"
        radius={4}
        isFormActive={isFormActive}
        overflow="visible"
      >
        {(isVisible || !locationRoomId) && (
          <>
            {isOrg && (
              <Select
                withAsterisk
                data={
                  currentLocation
                    ? [...locationsData, currentLocation]
                    : locationsData
                }
                disabled={isFormActive}
                label="Location"
                {...form.getInputProps("location")}
                styles={{ root: { width: "100%" } }}
                onChange={handleLocChange}
              />
            )}

            {isTaxable && (
              <MultiSelect
                disabled={isFormActive}
                data={
                  taxRates?.locationTaxRateMany?.map((val) => ({
                    value: val.id,
                    label: val.organizationTaxRate.name,
                  })) || []
                }
                label="Tax Rates"
                {...form.getInputProps("taxRates")}
              />
            )}

            <MultiSelect
              disabled={isFormActive}
              data={roomsData}
              label="Rooms"
              {...form.getInputProps("rooms")}
            />

            {!locationRoomId && (
              <Group position="apart" mt="md" style={{ marginTop: 30 }}>
                <Button
                  radius="md"
                  variant="outline"
                  onClick={() => modals.closeAll()}
                  style={{
                    flex: 1,
                    color: theme.colors.secondaryTextColor[6],
                  }}
                  sx={{
                    backgroundColor: theme.colors.secondary[6],
                    "&:hover": {
                      background: theme.colors.secondary[5],
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  radius="md"
                  type="submit"
                  disabled={createLoading || updateLoading}
                  style={{
                    flex: 1,
                  }}
                  sx={{
                    color: theme.colors.primaryTextColor[6],
                    "&:hover": {
                      backgroundColor: theme.colors.primary[4],
                    },
                  }}
                  loading={createLoading || updateLoading}
                >
                  {locationRoomId ? "Save" : "Create"}
                </Button>
              </Group>
            )}
          </>
        )}
      </FormCard>
    </>
  );
};

export default memo(LocationRoomDetail);
